import { Link } from 'react-router-dom';
import { technologies } from '../data/technologies';
import { projects } from '../data/projects';
import BenefitIcon from '../components/BenefitIcon';
import { useEffect, useRef, useState } from 'react';
import { setPageMeta } from '../utils/setPageMeta';

const publicUrl = process.env.PUBLIC_URL || '';

// Custom hook for scroll animations
function useScrollAnimation(threshold = 0.1, rootMargin = '0px') {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold, rootMargin }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold, rootMargin]);

  return { ref, isVisible };
}

// Counter animation component
function AnimatedCounter({ end, duration = 2000, isVisible }: { end: number; duration?: number; isVisible: boolean }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isVisible) return;
    
    let startTime: number | null = null;
    const startValue = 0;
    
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      setCount(Math.floor(progress * (end - startValue) + startValue));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [end, duration, isVisible]);

  return <span>{count}</span>;
}

// 3D Tilt Card Component
function TiltCard({ children, className, style }: { children: React.ReactNode; className?: string; style?: React.CSSProperties }) {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const rotateX = (y - centerY) / 10;
      const rotateY = (centerX - x) / 10;

      card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
    };

    const handleMouseLeave = () => {
      card.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) scale3d(1, 1, 1)';
    };

    card.addEventListener('mousemove', handleMouseMove);
    card.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      card.removeEventListener('mousemove', handleMouseMove);
      card.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <div ref={cardRef} className={className} style={{ ...style, transition: 'transform 0.1s ease-out' }}>
      {children}
    </div>
  );
}

// Custom Cursor Component
function CustomCursor() {
  const [position, setPosition] = useState({ x: -100, y: -100 });
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setPosition({ x: e.clientX, y: e.clientY });
      
      const target = e.target as HTMLElement;
      const isInteractive = target.closest('a, button, .magnetic-btn, .tech-link');
      setIsHovering(!!isInteractive);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <>
      <div 
        className={`custom-cursor ${isHovering ? 'hovering' : ''}`}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
      />
      <div 
        className={`custom-cursor-dot ${isHovering ? 'hovering' : ''}`}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
      />
    </>
  );
}

export default function Home() {
  const whoSection = useScrollAnimation(0.2);
  const techSection = useScrollAnimation(0.2);
  const goalSection = useScrollAnimation(0.2);
  const statsSection = useScrollAnimation(0.3);
  const projectsSection = useScrollAnimation(0.2);
  const [scrollY, setScrollY] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  
  // Featured projects - show top 4
  const featuredProjects = projects.slice(0, 4);

  useEffect(() => {
    setPageMeta({
      title: 'LeadingNG – Web, Mobile & Blockchain | Web3, Fintech & AI',
      description:
        'LeadingNG builds web and mobile apps, Web3 on Solana and Ethereum, Rust, TypeScript, AWS, fintech and AI. 18+ years experience. Auckland, New Zealand — clients worldwide.',
      canonicalPath: '/',
      keywords:
        'LeadingNG, software development, Web3 development, Solana developer, Ethereum dApp, Rust development, TypeScript, Node.js, AWS, fintech software, AI development, React Native, blockchain development, Auckland, New Zealand',
    });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      const scrolled = (window.scrollY / documentHeight) * 100;
      setScrollProgress(scrolled);
    };
    
    const handleMouseMove = (e: MouseEvent) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <>
      {/* Custom Cursor */}
      <CustomCursor />
      
      {/* Scroll Progress Indicator */}
      <div className="scroll-progress-bar" style={{ width: `${scrollProgress}%` }} aria-hidden="true" />

      {/* Animated SVG Background Elements with advanced parallax */}
      <div className="scroll-decoration" aria-hidden="true">
        <svg className="decoration-svg" viewBox="0 0 1200 1200" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="var(--color-secondary)" stopOpacity="0.3" />
              <stop offset="100%" stopColor="var(--color-primary)" stopOpacity="0.3" />
            </linearGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          <g style={{ transform: `translate(${scrollY * 0.1}px, ${scrollY * 0.05}px)` }}>
            <path
              className="decoration-path path-1"
              d="M 50 100 Q 300 50 600 200 T 1150 150"
              fill="none"
              stroke="url(#lineGradient)"
              strokeWidth="3"
            />
            <path
              className="decoration-path path-2"
              d="M 0 300 Q 200 250 400 350 T 800 300 T 1200 400"
              fill="none"
              stroke="url(#lineGradient)"
              strokeWidth="2"
            />
          </g>
          <g className="floating-shapes" style={{ transform: `translate(${Math.sin(scrollY * 0.002) * 20}px, ${scrollY * -0.15}px)` }}>
            <circle className="decoration-circle circle-1" cx="200" cy="150" r="4" fill="var(--color-secondary)" filter="url(#glow)" />
            <circle className="decoration-circle circle-2" cx="600" cy="200" r="6" fill="var(--color-primary)" filter="url(#glow)" />
            <circle className="decoration-circle circle-3" cx="1000" cy="100" r="5" fill="var(--color-secondary)" filter="url(#glow)" />
          </g>
          {/* Morphing shapes */}
          <g style={{ transform: `translate(${mousePos.x * 0.02}px, ${mousePos.y * 0.02}px)` }}>
            <path
              className="morph-shape"
              d="M 100 800 Q 150 750 200 800 T 300 800"
              fill="none"
              stroke="var(--color-primary)"
              strokeWidth="2"
              opacity="0.2"
            />
          </g>
        </svg>
        
        {/* Floating geometric shapes */}
        <div className="geometric-float" style={{ 
          transform: `translate(${scrollY * 0.3}px, ${Math.sin(scrollY * 0.005) * 30}px) rotate(${scrollY * 0.1}deg)`,
          left: '10%',
          top: '20%'
        }}>
          <div className="geo-shape geo-triangle" />
        </div>
        <div className="geometric-float" style={{ 
          transform: `translate(${scrollY * -0.2}px, ${Math.cos(scrollY * 0.003) * 40}px) rotate(${scrollY * -0.15}deg)`,
          right: '15%',
          top: '40%'
        }}>
          <div className="geo-shape geo-square" />
        </div>
        <div className="geometric-float" style={{ 
          transform: `translate(${scrollY * 0.15}px, ${Math.sin(scrollY * 0.004) * 25}px)`,
          left: '80%',
          top: '60%'
        }}>
          <div className="geo-shape geo-circle" />
        </div>
      </div>

      <section className="hero">
        <div className="hero-slides">
          <div className="hero-slide hero-slide-1" style={{ 
            transform: `translateY(${scrollY * 0.5}px) scale(${1 + scrollY * 0.0002})` 
          }} />
          <div className="hero-slide hero-slide-2" style={{ 
            transform: `translateY(${scrollY * 0.3}px) scale(${1 + scrollY * 0.0002})` 
          }} />
          <div className="hero-slide hero-slide-3" style={{ 
            transform: `translateY(${scrollY * 0.4}px) scale(${1 + scrollY * 0.0002})` 
          }} />
        </div>
        <div className="hero-overlay" aria-hidden="true" style={{
          opacity: Math.min(1, 0.6 + scrollY * 0.001)
        }} />
        <div className="hero-content" style={{
          transform: `translateY(${scrollY * 0.2}px)`,
          opacity: Math.max(0, 1 - scrollY * 0.002)
        }}>
          <div className="container text-center">
            <span className="hero-label animate-fade-in">
              18+ years · Clients worldwide
            </span>
            <h1 className="hero-title">
              <span className="hero-upper animate-slide-up" style={{ animationDelay: '0.1s' }}>
                We build
              </span>
              <span className="hero-middle animate-slide-up" style={{ animationDelay: '0.2s' }}>
                Web, Mobile and Blockchain Apps
              </span>
              <span className="hero-bottom animate-slide-up" style={{ animationDelay: '0.3s' }}>
                with a focus on Web3, Fintech and AI
              </span>
            </h1>
            <div className="hero-actions animate-fade-in" style={{ animationDelay: '0.5s' }}>
              <Link to="/contact-us" className="btn btn-cta btn-cta-primary hero-cta magnetic-btn">
                <span className="btn-text">Talk to us</span>
                <span className="btn-bg" />
              </Link>
              <a href="#who-section" className="hero-scroll" aria-label="Scroll to next section">
                Learn more
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Animated stats section */}
      <section ref={statsSection.ref} className={`section stats-section ${statsSection.isVisible ? 'visible' : ''}`}>
        <div className="container">
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number">
                <AnimatedCounter end={18} isVisible={statsSection.isVisible} />+
              </div>
              <div className="stat-label">Years Experience</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">
                <AnimatedCounter end={35} isVisible={statsSection.isVisible} />+
              </div>
              <div className="stat-label">Projects Delivered</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">
                <AnimatedCounter end={10} isVisible={statsSection.isVisible} />+
              </div>
              <div className="stat-label">Happy Clients</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">
                <AnimatedCounter end={6} isVisible={statsSection.isVisible} />+
              </div>
              <div className="stat-label">Countries Served</div>
            </div>
          </div>
        </div>
      </section>

      <section id="who-section" ref={whoSection.ref} className={`section who-section ${whoSection.isVisible ? 'visible' : ''}`}>
        <div className="container text-center">
          <span className="section-label scroll-reveal">
            Who we are
          </span>
          <h2 className="title scroll-reveal gradient-text" style={{ transitionDelay: '0.1s' }}>
            Built for ambitious projects
          </h2>
          <p className="intro scroll-reveal" style={{ transitionDelay: '0.2s' }}>
            LeadingNG is a software company with over 18 years of experience delivering technology solutions across industries and company sizes, for clients around the world. We focus on <strong>Web3</strong>, <strong>Fintech</strong> and <strong>AI</strong>, and bring proven expertise in platform engineering, mobile and web development. We are always ready to offer tailored solutions and turn challenges into opportunities.
          </p>
          <div className="benefits-grid">
            <TiltCard className="benefit-item scroll-reveal" style={{ transitionDelay: '0.3s' }}>
              <div className="benefit-icon"><BenefitIcon name="team" /></div>
              <h3 className="benefit-title">Skilled team</h3>
              <div className="benefit-glow" />
            </TiltCard>
            <TiltCard className="benefit-item scroll-reveal" style={{ transitionDelay: '0.4s' }}>
              <div className="benefit-icon"><BenefitIcon name="agile" /></div>
              <h3 className="benefit-title">Agile approach</h3>
              <div className="benefit-glow" />
            </TiltCard>
            <TiltCard className="benefit-item scroll-reveal" style={{ transitionDelay: '0.5s' }}>
              <div className="benefit-icon"><BenefitIcon name="quality" /></div>
              <h3 className="benefit-title">High quality code</h3>
              <div className="benefit-glow" />
            </TiltCard>
            <TiltCard className="benefit-item scroll-reveal" style={{ transitionDelay: '0.6s' }}>
              <div className="benefit-icon"><BenefitIcon name="overheads" /></div>
              <h3 className="benefit-title">No overheads</h3>
              <div className="benefit-glow" />
            </TiltCard>
          </div>
          <Link to="/about-us" className="btn btn-cta btn-cta-secondary scroll-reveal magnetic-btn" style={{ transitionDelay: '0.7s' }}>
            <span className="btn-text">More about us</span>
            <span className="btn-bg" />
          </Link>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section ref={projectsSection.ref} className={`section featured-projects-section ${projectsSection.isVisible ? 'visible' : ''}`}>
        {/* Decorative SVG elements */}
        <svg className="projects-decoration" viewBox="0 0 1200 800" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <defs>
            <linearGradient id="projectGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="var(--color-primary)" stopOpacity="0.15" />
              <stop offset="100%" stopColor="var(--color-secondary)" stopOpacity="0.15" />
            </linearGradient>
          </defs>
          {/* Curved lines */}
          <path
            className="projects-curve"
            d="M 0 100 Q 300 50 600 150 T 1200 100"
            fill="none"
            stroke="url(#projectGradient)"
            strokeWidth="2"
            opacity="0.3"
          />
          <path
            className="projects-curve"
            d="M 0 250 Q 400 200 800 300 T 1200 250"
            fill="none"
            stroke="url(#projectGradient)"
            strokeWidth="2"
            opacity="0.2"
          />
          {/* Geometric shapes - dice/cubes */}
          <g opacity="0.08">
            <rect x="100" y="50" width="40" height="40" fill="var(--color-primary)" transform="rotate(15 120 70)" />
            <rect x="1000" y="200" width="30" height="30" fill="var(--color-secondary)" transform="rotate(-20 1015 215)" />
            <rect x="150" y="600" width="35" height="35" fill="var(--color-primary)" transform="rotate(25 167.5 617.5)" />
            <rect x="950" y="650" width="45" height="45" fill="var(--color-secondary)" transform="rotate(-15 972.5 672.5)" />
          </g>
          {/* Dots/circles */}
          <circle cx="200" cy="400" r="5" fill="var(--color-primary)" opacity="0.1" />
          <circle cx="1050" cy="500" r="6" fill="var(--color-secondary)" opacity="0.1" />
          <circle cx="300" cy="150" r="4" fill="var(--color-primary)" opacity="0.1" />
        </svg>
        <div className="container text-center">
          <span className="section-label scroll-reveal">Featured Work</span>
          <h2 className="title scroll-reveal gradient-text" style={{ transitionDelay: '0.1s' }}>Recent Projects</h2>
          <p className="intro scroll-reveal" style={{ transitionDelay: '0.2s' }}>
            A selection of our recent work across Web3, Fintech, AI, and platform engineering.
          </p>
          <div className="featured-projects-grid">
            {featuredProjects.map((project, index) => (
              <div key={index} className="featured-project-card scroll-reveal" style={{ transitionDelay: `${0.3 + index * 0.1}s` }}>
                <div className="featured-project-content">
                  <h3 className="featured-project-title">{project.headline}</h3>
                  {project.link && (
                    <a href={project.link.url} target="_blank" rel="noopener noreferrer" className="featured-project-link">
                      {project.link.label} →
                    </a>
                  )}
                  <p className="featured-project-description">{project.description}</p>
                  <div className="featured-project-tech">
                    {project.technologies.slice(0, 5).map((tech, i) => (
                      <span key={i} className="featured-tech-tag">{tech}</span>
                    ))}
                    {project.technologies.length > 5 && (
                      <span className="featured-tech-more">+{project.technologies.length - 5} more</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Link to="/projects" className="btn btn-cta btn-cta-secondary magnetic-btn scroll-reveal" style={{ transitionDelay: '0.8s' }}>
            <span className="btn-text">View All Projects</span>
            <span className="btn-bg" />
          </Link>
        </div>
      </section>

      <section ref={goalSection.ref} className={`section goal-section ${goalSection.isVisible ? 'visible' : ''}`}>
        <div className="container text-center">
          <span className="section-label scroll-reveal">
            Our main goal
          </span>
          <h2 className="title scroll-reveal gradient-text" style={{ transitionDelay: '0.1s' }}>
            Trust and quality
          </h2>
          <p className="intro scroll-reveal" style={{ transitionDelay: '0.2s' }}>
            Our main goal is to be trustworthy and deliver high-quality services, meeting both our customers' requirements and the latest technology standards. We do our best to satisfy and exceed expectations—so our clients know they can rely on us for their next projects and we're happy to provide references.
          </p>
        </div>
      </section>

      <section ref={techSection.ref} className={`section tech-section ${techSection.isVisible ? 'visible' : ''}`}>
        <div className="container text-center">
          <span className="section-label scroll-reveal">
            Technologies we use
          </span>
          <h2 className="title scroll-reveal gradient-text" style={{ transitionDelay: '0.1s' }}>
            Stack we trust
          </h2>
          <p className="intro scroll-reveal" style={{ transitionDelay: '0.2s' }}>
            We focus on <strong>TypeScript</strong> and <strong>Rust</strong> for backend, smart contracts and tooling, plus React, React Native, Node.js, AWS, Terraform and serverless. We also use PHP, Laravel, PostgreSQL, MongoDB, and AI/RAG (e.g. AWS Bedrock) for Web3, Fintech and AI projects.
          </p>
          <div className="tech-strip scroll-reveal" style={{ transitionDelay: '0.3s' }}>
            <ul className="tech-list">
              {technologies.map((t, index) => (
                <li key={t.slug} className="tech-item" style={{ animationDelay: `${0.4 + index * 0.05}s` }}>
                  <Link to={`/technology/${t.slug}`} title={t.name} className="tech-link">
                    <img src={`${publicUrl}/assets/images/technologies/${t.image}`} alt={t.name} loading="lazy" />
                  </Link>
                </li>
              ))}
            </ul>
            <p className="tech-more">and many more…</p>
          </div>
        </div>
      </section>

      <section className="section logos-section hidden" aria-hidden="true">
        <div className="container text-center">
          <h2 className="title">Who we have worked with</h2>
          <p className="intro">Web &amp; mobile apps · Web3 &amp; metaverse · Fintech · AI · E-commerce · Platform &amp; identity</p>
          <ul className="client-logos">
            <li>
              <a href="https://www.donedusted.com" target="_blank" rel="noopener noreferrer">
                <img src={`${publicUrl}/assets/images/logos/logo_dd.png`} alt="Done & Dusted" />
              </a>
            </li>
            <li>
              <a href="https://www.mytaxback.co.nz" target="_blank" rel="noopener noreferrer">
                <img src={`${publicUrl}/assets/images/logos/logo_mytaxback.png`} alt="MyTaxBack" />
              </a>
            </li>
            <li>
              <a href="https://www.neighbourly.co.nz" target="_blank" rel="noopener noreferrer">
                <img src={`${publicUrl}/assets/images/logos/logo_neighbourly.jpg`} alt="Neighbourly" />
              </a>
            </li>
            <li>
              <a href="https://www.aotea.co.nz" target="_blank" rel="noopener noreferrer">
                <img src={`${publicUrl}/assets/images/logos/logo_aotea.jpg`} alt="Aotea" />
              </a>
            </li>
          </ul>
        </div>
      </section>

      <style>{`
        /* Custom Cursor */
        .custom-cursor,
        .custom-cursor-dot {
          position: fixed;
          pointer-events: none;
          z-index: 10000;
          transform: translate(-50%, -50%);
        }
        .custom-cursor {
          width: 40px;
          height: 40px;
          border: 2px solid var(--color-primary);
          border-radius: 50%;
          transition: width 0.3s ease, height 0.3s ease, border-color 0.3s ease;
          opacity: 0.5;
        }
        .custom-cursor.hovering {
          width: 60px;
          height: 60px;
          border-color: var(--color-secondary);
          opacity: 0.8;
        }
        .custom-cursor-dot {
          width: 6px;
          height: 6px;
          background: var(--color-primary);
          border-radius: 50%;
          transition: transform 0.15s ease, background 0.3s ease;
        }
        .custom-cursor-dot.hovering {
          transform: translate(-50%, -50%) scale(1.5);
          background: var(--color-secondary);
        }
        
        /* Hide custom cursor on mobile */
        @media (max-width: 768px) {
          .custom-cursor,
          .custom-cursor-dot {
            display: none;
          }
        }
        
        /* Scroll Progress Bar */
        .scroll-progress-bar {
          position: fixed;
          top: 0;
          left: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--color-primary), var(--color-secondary));
          z-index: 9999;
          transition: width 0.1s ease-out;
          box-shadow: 0 0 10px var(--color-primary);
        }

        /* Animated decorative background */
        .scroll-decoration {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 0;
          overflow: hidden;
        }
        .decoration-svg {
          position: absolute;
          width: 100%;
          height: 100%;
          opacity: 0.4;
        }
        .decoration-path {
          stroke-dasharray: 1000;
          stroke-dashoffset: 1000;
          animation: drawPath 3s ease-out forwards;
        }
        .decoration-path.path-1 {
          animation-delay: 0.5s;
        }
        .decoration-path.path-2 {
          animation-delay: 1s;
        }
        .decoration-circle {
          opacity: 0;
          animation: fadeInCircle 1s ease-out forwards;
          transition: transform 0.3s ease-out;
        }
        .decoration-circle.circle-1 {
          animation-delay: 1.5s;
        }
        .decoration-circle.circle-2 {
          animation-delay: 1.8s;
        }
        .decoration-circle.circle-3 {
          animation-delay: 2.1s;
        }
        @keyframes drawPath {
          to {
            stroke-dashoffset: 0;
          }
        }
        @keyframes fadeInCircle {
          to {
            opacity: 1;
          }
        }
        
        /* Floating geometric shapes */
        .geometric-float {
          position: fixed;
          pointer-events: none;
          will-change: transform;
        }
        .geo-shape {
          width: 60px;
          height: 60px;
          opacity: 0.15;
          filter: blur(1px);
        }
        .geo-triangle {
          width: 0;
          height: 0;
          border-left: 30px solid transparent;
          border-right: 30px solid transparent;
          border-bottom: 52px solid var(--color-primary);
          animation: float 8s ease-in-out infinite;
        }
        .geo-square {
          background: var(--color-secondary);
          border-radius: 8px;
          animation: float 10s ease-in-out infinite;
          animation-delay: -2s;
        }
        .geo-circle {
          background: var(--color-primary);
          border-radius: 50%;
          animation: float 12s ease-in-out infinite;
          animation-delay: -4s;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          25% { transform: translateY(-20px) rotate(5deg); }
          50% { transform: translateY(0) rotate(10deg); }
          75% { transform: translateY(20px) rotate(5deg); }
        }
        
        /* Morph shape animation */
        .morph-shape {
          animation: morphPath 8s ease-in-out infinite;
        }
        @keyframes morphPath {
          0%, 100% { d: path("M 100 800 Q 150 750 200 800 T 300 800"); }
          50% { d: path("M 100 820 Q 150 770 200 820 T 300 820"); }
        }

        /* Hero section with advanced parallax */
        .hero {
          position: relative;
          min-height: 480px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        .hero-slides { position: absolute; inset: 0; }
        .hero-slide {
          position: absolute;
          inset: 0;
          background-size: cover;
          background-position: center;
          will-change: transform;
          transition: transform 0.1s ease-out;
        }
        .hero-slide-1 { 
          background-image: url('${publicUrl}/assets/images/background/promo-background-1.jpg'); 
          animation: heroFade1 9s infinite; 
        }
        .hero-slide-2 { 
          background-image: url('${publicUrl}/assets/images/background/promo-background-2.jpg'); 
          animation: heroFade2 9s infinite; 
        }
        .hero-slide-3 { 
          background-image: url('${publicUrl}/assets/images/background/promo-background-3.jpg'); 
          animation: heroFade3 9s infinite; 
        }
        @keyframes heroFade1 { 0%, 33% { opacity: 1; } 34%, 100% { opacity: 0; } }
        @keyframes heroFade2 { 0%, 33% { opacity: 0; } 34%, 66% { opacity: 1; } 67%, 100% { opacity: 0; } }
        @keyframes heroFade3 { 0%, 66% { opacity: 0; } 67%, 100% { opacity: 1; } }
        .hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.6) 100%);
          z-index: 1;
          transition: opacity 0.3s ease-out;
        }
        .hero-content {
          position: relative;
          z-index: 2;
          color: #fff;
          text-shadow: 0 2px 8px rgba(0,0,0,0.4);
          will-change: transform, opacity;
          transition: transform 0.1s ease-out, opacity 0.1s ease-out;
        }

        /* Hero animations */
        .animate-fade-in {
          opacity: 0;
          animation: fadeIn 1s ease-out forwards;
        }
        .animate-slide-up {
          opacity: 0;
          transform: translateY(30px);
          animation: slideUp 0.8s ease-out forwards;
        }
        @keyframes fadeIn {
          to {
            opacity: 1;
          }
        }
        @keyframes slideUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .hero-label {
          display: inline-block;
          font-size: 0.8rem;
          font-weight: 600;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          opacity: 0.9;
          margin-bottom: 12px;
        }
        .hero-title { margin: 0 0 28px; }
        .hero-upper { display: block; font-size: 1.15rem; opacity: 0.95; }
        .hero-middle { display: block; font-size: 2.4rem; font-weight: 700; letter-spacing: -0.02em; line-height: 1.15; }
        .hero-bottom { display: block; font-size: 1.2rem; opacity: 0.92; margin-top: 4px; }
        .hero-actions { display: flex; flex-direction: column; align-items: center; gap: 20px; }
        
        /* Magnetic Button Effect */
        .magnetic-btn {
          position: relative;
          overflow: hidden;
          font-size: 1.05rem;
          padding: 16px 36px;
          transform-style: preserve-3d;
          transition: transform 0.3s ease;
        }
        .magnetic-btn .btn-text {
          position: relative;
          z-index: 2;
        }
        .magnetic-btn .btn-bg {
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at center, rgba(255,255,255,0.2), transparent);
          transform: scale(0);
          transition: transform 0.5s ease;
          z-index: 1;
        }
        .magnetic-btn:hover .btn-bg {
          transform: scale(2.5);
        }
        .magnetic-btn::after {
          content: '';
          position: absolute;
          inset: -2px;
          background: linear-gradient(45deg, 
            transparent, 
            rgba(255,255,255,0.1), 
            transparent);
          transform: translateX(-100%);
          transition: transform 0.6s ease;
        }
        .magnetic-btn:hover::after {
          transform: translateX(100%);
        }
        .magnetic-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(0,0,0,0.3);
        }
        
        .hero-cta { 
          font-size: 1.05rem; 
          padding: 16px 36px;
        }
        .hero-scroll {
          color: rgba(255,255,255,0.9);
          font-size: 0.9rem;
          font-weight: 600;
          text-decoration: none;
          transition: color 0.2s ease, opacity 0.2s ease, transform 0.3s ease;
          position: relative;
        }
        .hero-scroll:hover { 
          color: #fff; 
          text-decoration: none; 
          opacity: 1;
          transform: translateY(3px);
        }
        .hero-scroll::after {
          content: '↓';
          display: block;
          animation: bounce 2s infinite;
        }
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-10px); }
          60% { transform: translateY(-5px); }
        }

        /* Animated stats section */
        .stats-section {
          background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%);
          color: #fff;
          padding: 60px 20px;
          position: relative;
          overflow: hidden;
        }
        .stats-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
          opacity: 0.3;
        }
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 40px;
          max-width: 1000px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }
        .stat-item {
          text-align: center;
          opacity: 0;
          transform: translateY(30px) scale(0.9);
          transition: opacity 0.8s ease, transform 0.8s ease;
        }
        .stats-section.visible .stat-item {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
        .stats-section.visible .stat-item:nth-child(1) { transition-delay: 0.1s; }
        .stats-section.visible .stat-item:nth-child(2) { transition-delay: 0.2s; }
        .stats-section.visible .stat-item:nth-child(3) { transition-delay: 0.3s; }
        .stats-section.visible .stat-item:nth-child(4) { transition-delay: 0.4s; }
        .stat-number {
          font-size: 3.5rem;
          font-weight: 800;
          line-height: 1;
          margin-bottom: 12px;
          background: linear-gradient(180deg, #fff 0%, rgba(255,255,255,0.7) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .stat-label {
          font-size: 0.95rem;
          font-weight: 500;
          opacity: 0.95;
          letter-spacing: 0.02em;
        }

        /* Scroll reveal animations */
        .scroll-reveal {
          opacity: 0;
          transform: translateY(40px);
          transition: opacity 0.8s ease, transform 0.8s ease;
        }
        .visible .scroll-reveal {
          opacity: 1;
          transform: translateY(0);
        }

        .who-section { 
          background: #f5f6f8; 
          position: relative;
          z-index: 1;
        }
        .benefits-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          margin: 36px auto 36px;
          max-width: 920px;
        }
        .benefit-item {
          padding: 28px 20px;
          background: #fff;
          border-radius: var(--radius);
          box-shadow: var(--shadow-sm);
          border: 1px solid var(--color-border);
          position: relative;
          overflow: hidden;
          transform-style: preserve-3d;
        }
        .benefit-item::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(var(--color-primary-rgb), 0.1), transparent);
          transition: left 0.5s ease;
          z-index: 1;
        }
        .benefit-item:hover::before {
          left: 100%;
        }
        
        /* Benefit card glow effect */
        .benefit-glow {
          position: absolute;
          inset: -2px;
          background: linear-gradient(135deg, var(--color-primary), var(--color-secondary));
          border-radius: var(--radius);
          opacity: 0;
          z-index: -1;
          filter: blur(10px);
          transition: opacity 0.3s ease;
        }
        .benefit-item:hover .benefit-glow {
          opacity: 0.4;
        }
        
        .benefit-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 44px;
          height: 44px;
          margin: 0 auto 12px;
          color: var(--color-secondary);
          transition: transform 0.3s ease;
          position: relative;
          z-index: 2;
        }
        .benefit-item:hover .benefit-icon {
          transform: scale(1.1) rotate(5deg);
          animation: iconPulse 0.6s ease-in-out;
        }
        @keyframes iconPulse {
          0%, 100% { transform: scale(1.1) rotate(5deg); }
          50% { transform: scale(1.2) rotate(-5deg); }
        }
        .benefit-icon .benefit-icon-svg { width: 28px; height: 28px; display: block; }
        .benefit-icon .benefit-icon-svg svg { width: 100%; height: 100%; }
        .benefit-title { margin: 0; font-size: 1.05rem; font-weight: 700; color: #1a1a1a; }
        
        .tech-section { 
          background: #fff; 
          position: relative;
          z-index: 1;
          overflow: hidden;
        }
        .tech-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2300a0e3' fill-opacity='0.02'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
          opacity: 0.3;
          z-index: 0;
        }
        .tech-section > .container {
          position: relative;
          z-index: 1;
        }
        .tech-strip {
          background: #f5f6f8;
          border-radius: var(--radius);
          padding: 32px 24px;
          margin: 28px auto 0;
          max-width: 900px;
          border: 1px solid var(--color-border);
        }
        .tech-list {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 28px;
        }
        .visible .tech-item {
          opacity: 0;
          transform: scale(0.8) translateY(20px);
          animation: techItemAppear 0.6s ease-out forwards;
        }
        @keyframes techItemAppear {
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        .tech-link {
          display: block;
          position: relative;
          padding: 10px;
          border-radius: 8px;
          transition: background 0.3s ease;
        }
        .tech-link::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 8px;
          background: linear-gradient(135deg, var(--color-primary), var(--color-secondary));
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .tech-link:hover::before {
          opacity: 0.1;
        }
        .tech-list li img { 
          height: 56px; 
          width: auto; 
          opacity: 0.88; 
          transition: opacity 0.3s ease, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), filter 0.3s ease;
          filter: grayscale(0.3);
          position: relative;
          z-index: 1;
        }
        .tech-list a:hover img { 
          opacity: 1;
          transform: translateY(-8px) scale(1.1) rotate(2deg);
          filter: grayscale(0) drop-shadow(0 10px 20px rgba(0,0,0,0.2));
        }
        .tech-more { margin: 20px 0 0; font-size: 0.95rem; color: var(--color-text-muted); }
        
        /* Featured Projects Section */
        .featured-projects-section {
          background: #fff;
          position: relative;
          z-index: 1;
          overflow: hidden;
        }
        .featured-projects-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2300a0e3' fill-opacity='0.02'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
          opacity: 0.3;
          z-index: 0;
        }
        .featured-projects-section::after {
          content: '';
          position: absolute;
          inset: 0;
          background-image: 
            linear-gradient(135deg, transparent 48%, rgba(var(--color-primary-rgb), 0.03) 49%, rgba(var(--color-primary-rgb), 0.03) 51%, transparent 52%),
            linear-gradient(45deg, transparent 48%, rgba(var(--color-secondary-rgb), 0.03) 49%, rgba(var(--color-secondary-rgb), 0.03) 51%, transparent 52%);
          background-size: 100px 100px;
          opacity: 0.4;
          pointer-events: none;
          z-index: 0;
        }
        .featured-projects-section > .container {
          position: relative;
          z-index: 1;
          overflow: visible;
        }
        
        /* Decorative SVG for projects section */
        .projects-decoration {
          position: absolute;
          top: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 100%;
          max-width: 1200px;
          height: 100%;
          pointer-events: none;
          z-index: 0;
          opacity: 0.6;
        }
        .projects-curve {
          stroke-dasharray: 2000;
          stroke-dashoffset: 2000;
          animation: drawCurve 2s ease-out forwards;
        }
        .projects-curve:nth-child(2) {
          animation-delay: 0.5s;
        }
        @keyframes drawCurve {
          to {
            stroke-dashoffset: 0;
          }
        }
        .featured-projects-section {
          isolation: isolate;
        }
        .featured-projects-grid::before,
        .featured-projects-grid::after {
          content: '';
          position: absolute;
          border-radius: 8px;
          opacity: 0.06;
          pointer-events: none;
        }
        .featured-projects-grid::before {
          width: 80px;
          height: 80px;
          background: var(--color-primary);
          top: 10%;
          right: 5%;
          transform: rotate(15deg);
          animation: floatShape 10s ease-in-out infinite;
        }
        .featured-projects-grid::after {
          width: 60px;
          height: 60px;
          background: var(--color-secondary);
          bottom: 15%;
          left: 8%;
          transform: rotate(-20deg);
          animation: floatShape 12s ease-in-out infinite;
          animation-delay: -3s;
        }
        @keyframes floatShape {
          0%, 100% { transform: translateY(0) rotate(15deg); }
          50% { transform: translateY(-30px) rotate(25deg); }
        }
        .featured-projects-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 24px;
          margin: 36px auto 36px;
          max-width: 1000px;
          position: relative;
        }
        
        /* Curved line decorations for featured projects */
        .featured-projects-section .container::before,
        .featured-projects-section .container::after {
          content: '';
          position: absolute;
          width: 300px;
          height: 300px;
          border-radius: 50%;
          border: 2px solid rgba(var(--color-primary-rgb), 0.08);
          pointer-events: none;
        }
        .featured-projects-section .container::before {
          top: 10%;
          left: -150px;
          animation: rotateSlow 30s linear infinite;
        }
        .featured-projects-section .container::after {
          bottom: 10%;
          right: -150px;
          animation: rotateSlow 35s linear infinite reverse;
        }
        @keyframes rotateSlow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .featured-project-card {
          background: #fff;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          padding: 28px;
          transition: box-shadow 0.3s ease, transform 0.3s ease, border-color 0.3s ease;
          box-shadow: var(--shadow-sm);
          position: relative;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        .featured-project-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 4px;
          height: 100%;
          background: linear-gradient(180deg, var(--color-primary), var(--color-secondary));
          transform: scaleY(0);
          transition: transform 0.3s ease;
          transform-origin: top;
        }
        .featured-project-card:hover::before {
          transform: scaleY(1);
        }
        .featured-project-card:hover {
          box-shadow: var(--shadow-md);
          transform: translateY(-4px);
          border-color: var(--color-primary);
        }
        .featured-project-content {
          display: flex;
          flex-direction: column;
          flex: 1;
        }
        .featured-project-title {
          font-size: 1.15rem;
          font-weight: 700;
          color: var(--color-bg-dark);
          margin: 0 0 12px;
          transition: color 0.3s ease;
          line-height: 1.3;
        }
        .featured-project-card:hover .featured-project-title {
          color: var(--color-primary);
        }
        .featured-project-link {
          display: inline-block;
          font-size: 0.9rem;
          color: var(--color-link);
          margin-bottom: 12px;
          text-decoration: none;
          font-weight: 500;
          transition: color 0.3s ease;
        }
        .featured-project-link:hover {
          color: var(--color-primary);
          text-decoration: underline;
        }
        .featured-project-description {
          font-size: 0.95rem;
          color: var(--color-text-muted);
          line-height: 1.6;
          margin: 0 0 16px;
          flex: 1;
        }
        .featured-project-tech {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: auto;
        }
        .featured-tech-tag {
          font-size: 0.75rem;
          padding: 4px 10px;
          background: #f5f6f8;
          color: var(--color-secondary);
          border-radius: 999px;
          font-weight: 500;
          transition: background 0.3s ease, color 0.3s ease;
        }
        .featured-project-card:hover .featured-tech-tag {
          background: rgba(var(--color-primary-rgb), 0.1);
          color: var(--color-primary);
        }
        .featured-tech-more {
          font-size: 0.75rem;
          padding: 4px 10px;
          color: var(--color-text-muted);
          font-weight: 500;
          font-style: italic;
        }
        
        .goal-section { 
          background: #f5f6f8; 
          position: relative;
          z-index: 1;
          overflow: hidden;
        }
        .goal-section::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(var(--color-primary-rgb), 0.03) 0%, transparent 70%);
          animation: rotateGradient 20s linear infinite;
        }
        @keyframes rotateGradient {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .goal-section .intro {
          max-width: 800px;
          margin-left: auto;
          margin-right: auto;
          font-size: 1.1rem;
          line-height: 1.8;
          position: relative;
          z-index: 1;
        }
        
        /* Gradient animated text */
        .gradient-text {
          background: linear-gradient(90deg, 
            var(--color-primary) 0%, 
            var(--color-secondary) 50%, 
            var(--color-primary) 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: gradientShift 8s ease-in-out infinite;
        }
        @keyframes gradientShift {
          0%, 100% { background-position: 0% center; }
          50% { background-position: 100% center; }
        }
        
        .logos-section.hidden { display: none; }
        .client-logos {
          list-style: none;
          margin: 24px 0 0;
          padding: 0;
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 32px;
          align-items: center;
        }
        .client-logos img { max-height: 48px; width: auto; max-width: 140px; }

        /* Reduced motion preferences */
        @media (prefers-reduced-motion: reduce) {
          .hero-slide { 
            animation: none !important; 
            transform: none !important;
          }
          .hero-slide-1 { opacity: 1; }
          .hero-slide-2, .hero-slide-3 { opacity: 0; }
          .animate-fade-in,
          .animate-slide-up,
          .scroll-reveal,
          .stat-item,
          .tech-item {
            animation: none !important;
            opacity: 1 !important;
            transform: none !important;
          }
          .decoration-path,
          .decoration-circle,
          .geo-shape,
          .morph-shape,
          .projects-curve,
          .featured-projects-grid::before,
          .featured-projects-grid::after,
          .featured-projects-section .container::before,
          .featured-projects-section .container::after {
            animation: none !important;
            opacity: 0.2 !important;
            stroke-dashoffset: 0 !important;
          }
          .hero-scroll::after,
          .gradient-text {
            animation: none !important;
          }
          .scroll-progress-bar {
            display: none;
          }
        }

        @media (max-width: 768px) {
          .scroll-progress-bar {
            height: 2px;
          }
          .stats-grid { 
            grid-template-columns: repeat(2, 1fr); 
            gap: 32px;
          }
          .stat-number {
            font-size: 2.5rem;
          }
          .benefits-grid { grid-template-columns: repeat(2, 1fr); gap: 16px; }
          .benefit-item { padding: 22px 16px; }
          .featured-projects-grid { grid-template-columns: 1fr; gap: 20px; }
          .featured-project-card { padding: 24px; }
          .projects-decoration {
            display: none;
          }
          .featured-projects-section::after,
          .featured-projects-section .container::before,
          .featured-projects-section .container::after {
            display: none;
          }
          .hero { min-height: 420px; }
          .hero-middle { font-size: 1.75rem; }
          .tech-strip { padding: 24px 16px; }
          .tech-list { gap: 20px; }
          .tech-list li img { height: 44px; }
          
          /* Simplify animations on mobile */
          .geometric-float {
            display: none;
          }
          .decoration-svg {
            opacity: 0.2;
          }
        }
        
        @media (max-width: 480px) {
          .stat-number {
            font-size: 2rem;
          }
          .stat-label {
            font-size: 0.85rem;
          }
        }
      `}</style>
    </>
  );
}
